
import React, { useState, useCallback } from "react";
import {
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToWord,
  FormatDateToMDY,
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from "axios";
import ExcelJS from "exceljs";
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";

export default function CashReceiptsJournal() {
  //#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  //#endregion

  //#region Variables
  const [company, setCompany] = useState(null);

  // Default dates: beginning of current year to today
  const today = new Date();
  const localDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [dateFrom, setDateFrom] = useState(
    `${today.getFullYear()}-01-01`
  );
  const [dateTo, setDateTo] = useState(localDate(today));
  //#endregion

  //#region Generate

  const fetchReportData = useCallback(async () => {
    const body = {
      dateFrom,
      dateTo,
    };

    try {
      const response = await axios.post(
        `${apiUrl}/api/Report/report-data/cash-receipts-journal`,
        body,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching Report:", error);
      setFailMessage(
        error.response?.data?.message ||
        error.response?.data ||
        "Error fetching Report."
      );
      return null;
    }
  }, [dateFrom, dateTo]);

  const validateFilters = () => {
    if (!dateFrom || !dateTo || !company) {
      setFailMessage("Please fill up all fields.");
      return false;
    }

    if (new Date(dateFrom) > new Date(dateTo)) {
      setFailMessage("Date From cannot be later than Date To.");
      return false;
    }

    return true;
  };

  //#region PDF

  const generateBalance = async () => {
    if (!validateFilters()) return;

    setLoading(true);

    try {
      const responseData = await fetchReportData();

      if (!responseData) return;

      const pdfUrl = `${window.location.origin}/docs/CASH_RECEIPTS_JOURNAL.pdf`;

      const templateBytes = await fetch(pdfUrl).then((r) => {
        if (!r.ok) throw new Error("Report PDF template not found.");
        return r.arrayBuffer();
      });

      const templateDoc = await PDFDocument.load(templateBytes);
      const pdfDoc = await PDFDocument.load(templateBytes);

      pdfDoc.registerFontkit(fontkit);

      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";

        let truncated = String(text);
        const ellipsis = "...";

        while (
          truncated.length > 0 &&
          font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth
        ) {
          truncated = truncated.slice(0, -1);
        }

        return truncated.length < String(text).length
          ? truncated + ellipsis
          : String(text);
      };

      const drawHeader = (page) => {
        page.drawText(company.name?.toString() ?? "", {
          x: 35,
          y: 583,
          size: 16,
          font: calibriBold,
        });

        page.drawText(company.address?.toString() ?? "", {
          x: 35,
          y: 573,
          size: 8,
          font: calibriFont,
        });

        page.drawText(company.tin?.toString() ?? "", {
          x: 35,
          y: 563,
          size: 8,
          font: calibriFont,
        });

        page.drawText(
          `${FormatDateToWord(new Date(`${dateFrom}T00:00:00`))} to ${FormatDateToWord(new Date(`${dateTo}T00:00:00`))}`,
          {
            x: 85,
            y: 506,
            size: 8,
            font: calibriFont,
          }
        );

        page.drawText(FormatDateToWord(new Date()), {
          x: 85,
          y: 495,
          size: 8,
          font: calibriFont,
        });
      };

      let currentPage = pdfDoc.getPages()[0];
      drawHeader(currentPage);

      let yPosition = 446;
      const bottomThreshold = 80;
      const lineHeight = 12;

      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 446;
      };

      let totalAmount = 0;

      for (const record of responseData) {
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const documentNumber = truncateText(record.documentNumber ?? "", 80, calibriFont, 8);
        const postingDate = record.postingDate;
        const customerName = truncateText(record.customerName ?? "", 250, calibriFont, 8);
        const referenceNumber = truncateText(record.referenceNumber ?? "", 150, calibriFont, 8);
        const paymentMethodDescription = truncateText(record.paymentMethodDescription ?? "", 150, calibriFont, 8);
        const bankName = truncateText(record.bankName ?? "", 150, calibriFont, 8);
        const amount = record.amount;

        currentPage.drawText(documentNumber, {
          x: 40,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${FormatDateToMDY(postingDate)}`, {
          x: 113,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(customerName, {
          x: 185,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(referenceNumber, {
          x: 347,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(paymentMethodDescription, {
          x: 460,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(bankName, {
          x: 575,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${amount}`, {
          x: 675,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        totalAmount += (record.amount ?? 0)

        yPosition -= lineHeight;
      }

      // Grand Total
      if (yPosition - 25 < bottomThreshold) {
        await addNewPage();
      }

      currentPage.drawLine({
        start: { x: 35, y: yPosition },
        end: { x: 757, y: yPosition },
        thickness: 1,
      });

      yPosition -= 15;

      currentPage.drawText("GRAND TOTAL", {
        x: 575,
        y: yPosition,
        size: 9,
        font: calibriBold,
      });

      currentPage.drawText(`${FormatValueToDecimal(totalAmount)}`, {
        x: 675,
        y: yPosition,
        size: 8,
        font: calibriBold,
      });

      yPosition -= 18;

      // Page numbers
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;

      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], {
        type: "application/pdf",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "CASH_RECEIPTS_JOURNAL.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Report PDF Error:", error);
      setFailMessage(error.message || "Error processing PDF.");
    } finally {
      setLoading(false);
    }
  };

  //#endregion

  //#region Excel

  const exportExcel = async () => {
    if (!validateFilters()) return;

    setLoading(true);

    try {
      const responseData = await fetchReportData();

      if (!responseData || !responseData.length) {
        setFailMessage("No record found.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Cash Receipts Journal");

      // 1. Company Header
      worksheet.addRow([company.name?.toUpperCase() ?? ""]).font = {
        bold: true,
        size: 14,
      };
      worksheet.addRow([company.address ?? ""]);
      worksheet.addRow([company.tin ? `TIN: ${company.tin}` : ""]);
      worksheet.addRow([]); // Blank spacer

      // 2. Report Details Header
      worksheet.addRow(["CASH RECEIPTS JOURNAL"]).font = {
        bold: true,
        size: 12,
      };
      worksheet.addRow([
        `Period: ${FormatDateToWord(new Date(`${dateFrom}T00:00:00`))} to ${FormatDateToWord(new Date(`${dateTo}T00:00:00`))}`,
      ]);
      worksheet.addRow([
        `Date Printed: ${FormatDateToWord(new Date())}`,
      ]);
      worksheet.addRow([]); // Blank spacer

      // 3. Table Column Headers
      const headerRow = worksheet.addRow([
        "Document No.",
        "Posting Date",
        "Customer Name",
        "Reference No.",
        "Payment Method",
        "Bank Name",
        "Amount",
      ]);

      // Style the Header Row
      headerRow.eachCell((cell) => {
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" },
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "444444" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // 4. Data Rows
      let totalAmount = 0;

      responseData.forEach((record) => {
        const amountVal = Number(record.amount || 0);
        totalAmount += amountVal;

        const row = worksheet.addRow([
          record.documentNumber ?? "",
          FormatDateToMDY(record.postingDate),
          record.customerName ?? "",
          record.referenceNumber ?? "",
          record.paymentMethodDescription ?? "",
          record.bankName ?? "",
          amountVal,
        ]);

        // Center-align dates and doc numbers
        row.getCell(1).alignment = { horizontal: "center" };
        row.getCell(2).alignment = { horizontal: "center" };

        // Currency format on Amount column (Column 7)
        const amountCell = row.getCell(7);
        amountCell.numFmt = "#,##0.00";
        amountCell.alignment = { horizontal: "right" };
      });

      // 5. Grand Total Row
      const totalRow = worksheet.addRow([
        "",
        "",
        "",
        "",
        "",
        "GRAND TOTAL",
        totalAmount,
      ]);

      totalRow.font = { bold: true };

      // Label styling (Col 6)
      totalRow.getCell(6).alignment = { horizontal: "right" };
      totalRow.getCell(6).border = {
        top: { style: "thin" },
        bottom: { style: "double" },
      };

      // Amount styling (Col 7) with accounting double-underline
      const totalAmountCell = totalRow.getCell(7);
      totalAmountCell.numFmt = "#,##0.00";
      totalAmountCell.alignment = { horizontal: "right" };
      totalAmountCell.border = {
        top: { style: "thin" },
        bottom: { style: "double" },
      };

      // 6. Column Widths
      worksheet.columns = [
        { width: 18 }, // Document No.
        { width: 15 }, // Posting Date
        { width: 35 }, // Customer Name
        { width: 22 }, // Reference No.
        { width: 22 }, // Payment Method
        { width: 22 }, // Bank Name
        { width: 18 }, // Amount
      ];

      // 7. Generate and Trigger Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const filename = `Cash_Receipts_Journal_${dateFrom}_to_${dateTo}.xlsx`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Report Excel Error:", error);
      setFailMessage("Error generating Excel file.");
    } finally {
      setLoading(false);
    }
  };

  //#endregion

  //#endregion

  //#region Access Template

  const accountData = JSON.parse(localStorage.getItem("accountData"));

  if (!accountData?.accessTemplate?.hasCashReceiptsJournal) {
    return <NoAccess />;
  }

  //#endregion

  //#region UI

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon
                fontSize="large"
                className="text-theme-color"
              />
              Cash Receipts Journal
            </h2>

            <p className="text-gray-500 mt-1">
              Detailed book of cash and bank collections.
            </p>
          </div>
        </div>

        {/* Noticeable Note */}
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/80 p-3.5 text-blue-900 shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 flex-shrink-0 text-blue-600 mt-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm">
            <span className="font-bold">Important:</span> Make sure you configure the{" "}
            <span className="font-semibold underline decoration-blue-400 underline-offset-2">
              Cash Accounts
            </span>{" "}
            in the <strong>Chart of Accounts Module</strong>.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Date From <span className="text-red-500">*</span>
            </label>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Date To <span className="text-red-500">*</span>
            </label>

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Company <span className="text-red-500">*</span>
            </label>

            <CompanySelect
              data={company}
              setData={setCompany}
              isEditable={true}
            />
          </div>

        </div>

        <div className="mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 px-2">
            Generate Reports
          </h2>

          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-xs font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-2">Report Category</div>
            <div className="col-span-5">PDF Reports</div>
            <div className="col-span-5 border-l border-gray-100">
              Excel Reports
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="col-span-2">
                <span className="text-sm font-bold text-gray-700">
                  Summary
                </span>
              </div>

              <div className="col-span-5 flex flex-wrap gap-2">
                <button
                  onClick={generateBalance}
                  className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all active:scale-95"
                >
                  PDF Summary
                </button>
              </div>

              <div className="col-span-5 flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0">
                <button
                  onClick={exportExcel}
                  className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                >
                  Excel Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />

        <FailMessage
          message={failMessage}
          onClose={() => setFailMessage("")}
        />
      </div>

      {loading && <Loading />}
    </>
  );

  //#endregion
}
